import { Injectable } from '@angular/core';

import { APP_CONSTANTS } from '../constants/app.constants';
import { UserRole } from '../models/user.model';

@Injectable({ providedIn: 'root' })
export class TokenStorageService {
  private readonly maxTokenLength = 8192;

  getToken(): string | null {
    const token = sessionStorage.getItem(APP_CONSTANTS.tokenKey);
    if (!token || !this.isValid(token)) {
      this.clear();
      return null;
    }
    return token;
  }

  setToken(token: string, expectedRole?: UserRole): boolean {
    const normalized = token.trim();
    if (!this.isValid(normalized, expectedRole)) {
      this.clear();
      return false;
    }

    localStorage.removeItem(APP_CONSTANTS.tokenKey);
    sessionStorage.setItem(APP_CONSTANTS.tokenKey, normalized);
    return true;
  }

  clear(): void {
    sessionStorage.removeItem(APP_CONSTANTS.tokenKey);
    localStorage.removeItem(APP_CONSTANTS.tokenKey);
  }

  getPayload(token = sessionStorage.getItem(APP_CONSTANTS.tokenKey)): Record<string, unknown> | null {
    if (!token) return null;
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    return this.decodePart(parts[1]);
  }

  getRole(): UserRole | null {
    const payload = this.getPayload();
    const claim = payload?.['role'] ?? payload?.['user_role'] ?? payload?.['usertype'] ?? payload?.['type'];
    const role = typeof claim === 'string' ? claim.toLowerCase() : '';
    return role === 'admin' || role === 'user' ? role : null;
  }

  isExpired(): boolean {
    const expiry = this.getPayload()?.['exp'];
    return typeof expiry !== 'number' || expiry * 1000 <= Date.now();
  }

  isValid(token: string, expectedRole?: UserRole): boolean {
    if (!token || token.length > this.maxTokenLength || !/^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(token)) return false;

    const parts = token.split('.');
    const header = this.decodePart(parts[0]);
    const payload = this.decodePart(parts[1]);
    if (!header || !payload || String(header['alg'] ?? '').toLowerCase() === 'none') return false;

    const now = Math.floor(Date.now() / 1000);
    const expiry = payload['exp'];
    const notBefore = payload['nbf'];
    if (typeof expiry !== 'number' || expiry <= now) return false;
    if (typeof notBefore === 'number' && notBefore > now + 30) return false;

    if (expectedRole) {
      const claim = payload['role'] ?? payload['user_role'] ?? payload['usertype'] ?? payload['type'];
      if (typeof claim !== 'string' || claim.toLowerCase() !== expectedRole) return false;
    }
    return true;
  }

  private decodePart(value: string): Record<string, unknown> | null {
    try {
      const base64 = value.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(value.length / 4) * 4, '=');
      const decoded = decodeURIComponent(Array.from(atob(base64), (char) => `%${char.charCodeAt(0).toString(16).padStart(2, '0')}`).join(''));
      const parsed: unknown = JSON.parse(decoded);
      return parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed as Record<string, unknown> : null;
    } catch {
      return null;
    }
  }
}
